import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ImageStyle } from 'react-native';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Image,
  StyleSheet,
  Platform,
  Linking,
  useWindowDimensions,
} from 'react-native';
import type { LaunchSecureDto, PatinfoProfileDto, PatinfoPutRequestDto, PatinfoHistoryDto } from '../../../types/api/patinfo';
import { getPhiValue, normalizeHistoryAdminResponse } from '../../../types/api/patinfo';
import type { AccountPlaDto, AccountSraDto, AdminHtmlAnswerDto, MVarDto } from '../../../types/api';
import {
  buildClientProfilePutPayload,
  buildInitialScheduleStartDates,
  buildInitialSelectedScheduleIds,
  getClientDisplayName,
  profileToClientEditForm,
  type ClientProfileEditForm,
  type ClientTouchpointSchedule,
} from '../../../services/clients/ClientFormBuilder';
import {
  buildCancelLaunchRequest,
  buildScheduleLaunchRequest,
  buildSendNowLaunchRequest,
  resolveCurrentBaseUrl,
} from '../../../services/clients/ClientLaunchBuilder';
import { Base64ImageStorage, extractImgSrc } from '../../../storage/Base64ImageStorage';
import {
  getCachedAssessmentsBySourceId,
  loadAssessmentsBySourceId,
  resolveHistoryItemReport,
  resolveDefaultReportId,
  ASSESSMENT_CONTENT_UPDATED_EVENT,
} from '../../../services/clients/ClientReportAssessments';
import type { AdminAssessmentDto, AdminAssessmentReportDto } from '../../../types/adminAssessment';
import type { AdminResponseDto } from '../../../types/AdminResponseDto';
import type { ClientEnableFlags } from './ClientEnableFlags';
import { EDITOR_COLORS, editorText, webCheckboxStyle, webControlStyle } from '../../../styles/editorTheme';
import { SkinFeaturesContext } from '../../../contexts/SkinFeaturesContext';
import { PDF_ICON_IMAGE_ID } from '../../../utils/reportPdfIcon';

export interface ViewReportParams {
  profile: PatinfoProfileDto;
  adminResponse: AdminResponseDto;
  assessmentDefinition: AdminAssessmentDto | null;
  selectedReport: AdminAssessmentReportDto | null;
  /** 'pdf' — generate the report as a PDF in a new browser tab; the current tab/view never
   * changes. 'inline' — switch the current tab to the full ReportView UI (with a Back button)
   * showing just selectedReport; no PDF is generated. Two separate icons trigger these — see
   * ProfileRowEditor's Reports table "View" column. Defaults to 'pdf' when omitted, so any
   * existing caller that doesn't set this keeps today's PDF-only behavior. */
  mode?: 'pdf' | 'inline';
}

interface ProfileRowEditorProps {
  profile: PatinfoProfileDto;
  onSave: (payload: PatinfoPutRequestDto) => Promise<void>;
  onSendNow: (profile: PatinfoProfileDto, request: LaunchSecureDto) => Promise<void>;
  onCancelLaunch: (profile: PatinfoProfileDto, request: LaunchSecureDto) => Promise<void>;
  onViewReport?: (params: ViewReportParams) => void;
  enableFlags: ClientEnableFlags;
  initiallyExpanded?: boolean;
  sendAssessmentOptions?: AdminHtmlAnswerDto[];
  officeOnlineSraMembers?: AccountSraDto[];
  assessmentOptions?: AccountPlaDto[];
  clientPhiFields?: MVarDto[];
  genderPhiOptions?: AdminHtmlAnswerDto[];
  /** Account-level active touchpoint schedules (AccountSettings.Registries) — the source
   * list for the Schedule section's "Enable Touchpoint Schedules" column. */
  touchpointSchedules?: ClientTouchpointSchedule[];
  /**
   * Overrides the summary row's "Last Report"/"View Report" cells to reflect one specific
   * administration instead of this profile's own most-recent report — used by Recent Reports,
   * where each row represents a single administration (which may not be this profile's latest,
   * especially when the same profile appears more than once in the feed).
   */
  summaryReportOverride?: { testDate: string; onViewReport: (mode: 'pdf' | 'inline') => void };
}

interface ToggleArrowIcons {
  upSrc: string | null;
  downSrc: string | null;
  loaded: boolean;
}

interface PdfIconState {
  src: string | null;
  loaded: boolean;
}

type MobileDateTimePickerType = React.ComponentType<{
  value: Date;
  mode: 'date';
  display: 'default';
  onChange: (event: any, date?: Date) => void;
}>;

type SendAssessmentMethod = string;
type SendOptionKind = 'phone' | 'email' | 'app' | 'generic';

// TouchPointScheduleDto.Type — matches SCHEDULE_TYPE_OPTIONS in TouchpointsView.tsx.
const SCHEDULE_TYPE_MONITOR = '1';

const UP_ARROW_IMAGE_ID = '101';
const DOWN_ARROW_IMAGE_ID = '100';
const ALL_EDIT_SECTION_LABELS = [
  'Client Info',
  'Schedule',
  'Flags',
  'Extended Info',
  'Connections',
  'Messages',
  'Reports',
] as const;
type EditSectionLabel = typeof ALL_EDIT_SECTION_LABELS[number];

const resolveSraMemberLabel = (sra: AccountSraDto): string => {
  return sra.Name || sra.Email || sra.SraId;
};

const resolvePlaLabel = (pla: AccountPlaDto): string => {
  return pla.DName || pla.PlaId;
};

const getPlaKey = (pla: AccountPlaDto): string => {
  return `${pla.PlaId}:${pla.SCode}`;
};

const getSendMethodOptionKey = (option: AdminHtmlAnswerDto): string => {
  return (option.KeyValue || option.DataValue || option.AText || '').trim();
};

const isCopyPasteOption = (option: AdminHtmlAnswerDto): boolean => {
  // Match by substring, not exact equality: this option is picked from a fixed
  // catalog entry in Account > Assessment Options (How Contact), not free-typed,
  // so its label can be worded as "Copy/Paste", "Copy/Paste Link", etc. An exact
  // match to 'copypaste' broke as soon as the catalog label carried any extra
  // wording — the option then silently fell through to the generic radio-button
  // path instead of being pulled out as its own checkbox.
  const normalized = (option.AText || '').trim().toLowerCase().replace(/[^a-z]/g, '');
  return normalized.includes('copypaste');
};

const resolveSendMethodOptionKind = (label: string): SendOptionKind => {
  const normalized = (label || '').trim().toLowerCase();
  if (normalized.includes('email')) return 'email';
  if (normalized.includes('app')) return 'app';
  if (normalized.includes('text') || normalized.includes('sms') || normalized.includes('phone')) return 'phone';
  return 'generic';
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmailAddress = (value: string): boolean => EMAIL_PATTERN.test((value || '').trim());

// Not a full international-phone validator — just enough to catch blank/garbage input while
// tolerating common formatting (spaces, dashes, parens, a leading +).
const isValidPhoneNumber = (value: string): boolean => (value || '').replace(/\D/g, '').length >= 10;

const EXCLUDED_PHI_FIELD_NAMES = new Set([
  'patid', 'fname', 'lname', 'email', 'phone', 'cellphone',
  'howcontact', 'copypaste', 'localsra',
]);

/**
 * Display override for the copy/paste send option, regardless of the catalog's
 * exact AText wording — the UI now also surfaces the secure link itself, so the
 * label is clarified to say so no matter what Assessment Options has it labeled.
 */
const COPY_PASTE_LINK_LABEL = 'Copy/Paste Link';

const FALLBACK_GENDER_OPTIONS: AdminHtmlAnswerDto[] = [
  { AText: 'Male', KeyValue: '1', DataValue: '1', LanguageTypeId: '' },
  { AText: 'Female', KeyValue: '2', DataValue: '2', LanguageTypeId: '' },
];

const normalizePhiFieldName = (name: string): string => (name || '').trim().toLowerCase();

// Matches ClientFormBuilder.ts's own regid normalization — selectedScheduleIds/
// scheduleStartDates are keyed this way so lookups here line up with how they were seeded.
// String()-coerces before trim() — see matching comment on ClientFormBuilder.ts's own
// normalizeScheduleId; a numeric wire value would otherwise throw here too.
const normalizeScheduleKey = (value: string | number | null | undefined): string =>
  (value === null || value === undefined ? '' : String(value)).trim().toLowerCase();

const resolvePhiFieldLabel = (field: MVarDto): string => field.DName || field.Name;

const isGenderFieldName = (name: string): boolean => {
  const normalized = normalizePhiFieldName(name);
  return normalized === 'gender' || normalized === 'phigender';
};

let cachedToggleArrowIcons: ToggleArrowIcons | null = null;
let toggleArrowIconsLoadPromise: Promise<ToggleArrowIcons> | null = null;

const EMPTY_TOGGLE_ARROWS: ToggleArrowIcons = {
  upSrc: null,
  downSrc: null,
  loaded: false,
};


let cachedPdfIcon: PdfIconState | null = null;
let pdfIconLoadPromise: Promise<PdfIconState> | null = null;

const EMPTY_PDF_ICON: PdfIconState = {
  src: null,
  loaded: false,
};

const resolveRuntimeStorage = () => {
  return (globalThis as any).__proGroupsStorage
    ?? (globalThis as any).__childNavStorage
    ?? null;
};

const copySecureLinkToClipboard = async (link: string): Promise<boolean> => {
  try {
    if (typeof navigator !== 'undefined' && (navigator as any).clipboard?.writeText) {
      await (navigator as any).clipboard.writeText(link);
      return true;
    }
  } catch (error) {
    console.warn('ProfileRowEditor: navigator.clipboard.writeText failed, falling back', error);
  }

  try {
    if (typeof document !== 'undefined') {
      const textarea = document.createElement('textarea');
      textarea.value = link;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      return copied;
    }
  } catch (error) {
    console.warn('ProfileRowEditor: fallback clipboard copy failed', error);
  }

  return false;
};

const resolveImageSrc = (stored: string | null | undefined): string | null => {
  if (!stored) return null;

  const trimmed = stored.trim();
  if (!trimmed) return null;

  // Common shape from backend/base64 image storage: full <img ... src="data:..." />
  const extracted = extractImgSrc(trimmed);
  if (extracted) {
    return extracted;
  }

  // Already a usable data URI
  if (trimmed.startsWith('data:image/')) {
    return trimmed;
  }

  // Raw base64 payload without prefix
  if (/^[A-Za-z0-9+/=\s]+$/.test(trimmed) && trimmed.length > 80) {
    return `data:image/png;base64,${trimmed.replace(/\s+/g, '')}`;
  }

  return null;
};

const loadToggleArrowIcons = async (): Promise<ToggleArrowIcons> => {
  if (cachedToggleArrowIcons) {
    console.log('🖼️ ProfileRowEditor: using cached toggle arrow icons', {
      hasDown: !!cachedToggleArrowIcons.downSrc,
      hasUp: !!cachedToggleArrowIcons.upSrc,
    });
    return cachedToggleArrowIcons;
  }

  if (toggleArrowIconsLoadPromise) {
    console.log('🖼️ ProfileRowEditor: awaiting in-flight toggle arrow icon load');
    return toggleArrowIconsLoadPromise;
  }

  toggleArrowIconsLoadPromise = (async () => {
    try {
      const storage = resolveRuntimeStorage();
      console.log('🖼️ ProfileRowEditor: loading toggle arrows from Base64ImageStorage', {
        downId: DOWN_ARROW_IMAGE_ID,
        upId: UP_ARROW_IMAGE_ID,
        hasStorage: !!storage,
        storageType: storage?.constructor?.name ?? 'none',
      });

      if (!storage) {
        console.warn('🖼️ ProfileRowEditor: no runtime storage found, using fallback arrow glyphs');
        cachedToggleArrowIcons = { ...EMPTY_TOGGLE_ARROWS, loaded: true };
        return cachedToggleArrowIcons;
      }

      const imageStorage = new Base64ImageStorage(storage);
      const [downArrow, upArrow] = await Promise.all([
        imageStorage.getImage(DOWN_ARROW_IMAGE_ID),
        imageStorage.getImage(UP_ARROW_IMAGE_ID),
      ]);

      console.log('🖼️ ProfileRowEditor: Base64 image lookup results', {
        foundDown: !!downArrow,
        foundUp: !!upArrow,
        downName: downArrow?.dName ?? null,
        upName: upArrow?.dName ?? null,
        downBase64Length: downArrow?.base64?.length ?? 0,
        upBase64Length: upArrow?.base64?.length ?? 0,
      });

      const downSrc = resolveImageSrc(downArrow?.base64);
      const upSrc = resolveImageSrc(upArrow?.base64);

      console.log('🖼️ ProfileRowEditor: resolved image src values', {
        hasDownSrc: !!downSrc,
        hasUpSrc: !!upSrc,
        downSrcPrefix: downSrc ? downSrc.substring(0, 30) : null,
        upSrcPrefix: upSrc ? upSrc.substring(0, 30) : null,
      });

      cachedToggleArrowIcons = {
        downSrc,
        upSrc,
        loaded: true,
      };
      return cachedToggleArrowIcons;
    } catch (error) {
      console.error('🖼️ ProfileRowEditor: failed to load Base64 toggle arrows', error);
      cachedToggleArrowIcons = { ...EMPTY_TOGGLE_ARROWS, loaded: true };
      return cachedToggleArrowIcons;
    } finally {
      toggleArrowIconsLoadPromise = null;
    }
  })();

  return toggleArrowIconsLoadPromise;
};

const loadPdfIcon = async (): Promise<PdfIconState> => {
  if (cachedPdfIcon) {
    return cachedPdfIcon;
  }

  if (pdfIconLoadPromise) {
    return pdfIconLoadPromise;
  }

  pdfIconLoadPromise = (async () => {
    try {
      const storage = resolveRuntimeStorage();
      if (!storage) {
        cachedPdfIcon = { ...EMPTY_PDF_ICON, loaded: true };
        return cachedPdfIcon;
      }

      const imageStorage = new Base64ImageStorage(storage);
      const image = await imageStorage.getImage(PDF_ICON_IMAGE_ID);
      cachedPdfIcon = { src: resolveImageSrc(image?.base64), loaded: true };
      return cachedPdfIcon;
    } catch (error) {
      console.error('ProfileRowEditor: failed to load PDF icon image', error);
      cachedPdfIcon = { ...EMPTY_PDF_ICON, loaded: true };
      return cachedPdfIcon;
    } finally {
      pdfIconLoadPromise = null;
    }
  })();

  return pdfIconLoadPromise;
};

const formatDate = (raw: string): string => {
  if (!raw) return '—';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  return parsed.toLocaleDateString();
};

// Explicit mm/dd/yyyy hh:mm:ss (not locale-dependent) — used for RecUTM completion
// timestamps (Reports table "Completed" / row-header "Last Report"), where the time
// of day matters; formatDate above (date-only) still covers schedule/due dates.
const formatDateTime = (raw?: string): string => {
  if (!raw) return '—';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  const yyyy = parsed.getFullYear();
  const hh = String(parsed.getHours()).padStart(2, '0');
  const min = String(parsed.getMinutes()).padStart(2, '0');
  return `${mm}/${dd}/${yyyy} ${hh}:${min}`;
};

// LaunchNotifyEventDto.Type codes: 200/270 are the email/text variants of the initial
// notification, 201/271 the email/text variants of a reminder (see ClientLaunchBuilder.ts).
const NOTIFY_TYPE_LABELS: Record<string, string> = {
  '200': 'Initial',
  '270': 'Initial',
  '201': 'Reminder',
  '271': 'Reminder',
};

const resolveNotifyTypeLabel = (type: string): string => {
  return NOTIFY_TYPE_LABELS[type] ?? type;
};

const getNextTouchpoint = (profile: PatinfoProfileDto): string => {
  const scheduleNext = (profile.schedule ?? [])
    .map(item => item.schednext)
    .find(Boolean);
  if (scheduleNext) {
    return formatDate(scheduleNext);
  }

  const launchNext = (profile.launches ?? [])
    .map(item => item.DueDate)
    .find(Boolean);
  if (launchNext) {
    return formatDate(launchNext);
  }

  return '—';
};

const sortHistoryByRecentFirst = (history: PatinfoHistoryDto[]): PatinfoHistoryDto[] => {
  return [...history].sort((a, b) => {
    const dateA = new Date(a.Admin?.RecUTM || 0).getTime();
    const dateB = new Date(b.Admin?.RecUTM || 0).getTime();
    return dateB - dateA;
  });
};

const getLastReport = (profile: PatinfoProfileDto): PatinfoHistoryDto | null => {
  const history = profile.history ?? [];
  if (history.length === 0) {
    return null;
  }

  const top = sortHistoryByRecentFirst(history)[0];
  if (!top?.Admin?.RecId) {
    return null;
  }

  return top;
};

export const ProfileRowEditor: React.FC<ProfileRowEditorProps> = ({
  profile,
  onSave,
  onSendNow,
  onCancelLaunch,
  onViewReport,
  enableFlags,
  initiallyExpanded = false,
  sendAssessmentOptions = [],
  officeOnlineSraMembers = [],
  assessmentOptions = [],
  clientPhiFields = [],
  genderPhiOptions = [],
  touchpointSchedules = [],
  summaryReportOverride,
}) => {
  const isWeb = Platform.OS === 'web';
  const showLocalSendOption = officeOnlineSraMembers.length > 0;
  const additionalPhiFields = useMemo(
    () => clientPhiFields.filter((field) => !EXCLUDED_PHI_FIELD_NAMES.has(normalizePhiFieldName(field.Name))),
    [clientPhiFields]
  );
  const genderOptions = genderPhiOptions.length > 0 ? genderPhiOptions : FALLBACK_GENDER_OPTIONS;
  // Web selects/date inputs all render inside the section sub-cards, so they take the section
  // tint rather than webControlStyle's default white.
  const webInlineControlStyle = {
    ...webControlStyle,
    backgroundColor: EDITOR_COLORS.panelSectionBackground,
    flex: 1,
  } as any;
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [form, setForm] = useState<ClientProfileEditForm>(
    () => profileToClientEditForm(profile, additionalPhiFields.map((field) => field.Name))
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSendingNow, setIsSendingNow] = useState(false);
  const [sendNowError, setSendNowError] = useState<string | null>(null);
  const [sendNowOk, setSendNowOk] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [isPatidEditable, setIsPatidEditable] = useState(false);
  const [selectedAssessmentKey, setSelectedAssessmentKey] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [showMobileDueDatePicker, setShowMobileDueDatePicker] = useState(false);
  const [activeMobileDobField, setActiveMobileDobField] = useState<string | null>(null);
  const [activeMobileScheduleDateKey, setActiveMobileScheduleDateKey] = useState<string | null>(null);
  const [MobileDateTimePicker, setMobileDateTimePicker] = useState<MobileDateTimePickerType | null>(null);
  // Seeded from profile.schedule (PatinfoScheduleDto[]) — which touchpoint schedules this
  // client is currently enrolled in, keyed by normalized regid.
  const [selectedScheduleIds, setSelectedScheduleIds] = useState<Record<string, boolean>>(
    () => buildInitialSelectedScheduleIds(profile)
  );
  const [scheduleStartDates, setScheduleStartDates] = useState<Record<string, string>>(
    () => buildInitialScheduleStartDates(profile)
  );
  const [toggleArrowIcons, setToggleArrowIcons] = useState<ToggleArrowIcons>(cachedToggleArrowIcons ?? EMPTY_TOGGLE_ARROWS);
  const [pdfIcon, setPdfIcon] = useState<PdfIconState>(cachedPdfIcon ?? EMPTY_PDF_ICON);
  // Skin's enablereportpdf: the PDF action moves to the ReportView header, so the PDF icon comes
  // out of the summary row's "View Report" column and the Reports table's "View" column below.
  const { enableReportPdf, isProAccount } = useContext(SkinFeaturesContext);
  // Narrow-screen layout: the expanded editor's fixed-width label column (and the Schedule
  // section's fixed column gap) eat a disproportionate share of the row once the section card
  // itself is narrow, squeezing (or overflowing past) the content column — see the *Narrow
  // style overrides applied where those cells render. Mirrors AdminView.tsx's own
  // PHONE_MAX_WIDTH/isPhone breakpoint.
  const { width: windowWidth } = useWindowDimensions();
  const isNarrow = windowWidth < 500;
  const [assessmentsBySourceId, setAssessmentsBySourceId] = useState<Map<string, AdminAssessmentDto>>(getCachedAssessmentsBySourceId() ?? new Map());
  const [selectedReportIdByRecId, setSelectedReportIdByRecId] = useState<Record<string, string>>({});

  const displayName = getClientDisplayName(profile);
  const nextTouchpoint = getNextTouchpoint(profile);
  const lastReport = getLastReport(profile);
  const idNumber = form.patid || '—';
  // howcontact/copypaste/localsra are persisted PHI fields (form is their single
  // source of truth) rather than local-only useState — otherwise they reset to
  // defaults on every fresh profile load (e.g. after a Client Search), silently
  // discarding the user's previous send-method selection.
  const sendMethod: SendAssessmentMethod = form.howcontact;
  // Drives the email-type-code -> text-type-code remap in ClientLaunchBuilder (Email=200,
  // Text=270, etc.) — 'Local' (in-office) and any option whose label isn't recognized as
  // email/text fall back to the email-flavored codes, matching Send Now's prior fixed '200'.
  const selectedHowContactOption = sendAssessmentOptions.find((option) => getSendMethodOptionKey(option) === sendMethod);
  const howContactKind = selectedHowContactOption ? resolveSendMethodOptionKind(selectedHowContactOption.AText) : null;
  const isTextHowContact = howContactKind === 'phone';
  const isEmailHowContact = howContactKind === 'email';
  const selectedOfficeSraId = form.localsra;
  const connectedProfileName = (profile.connections ?? []).find(
    conn => !!(conn.destpracticedname || conn.srcpracticedname)
  );
  const connectedProfileDisplay = connectedProfileName?.destpracticedname
    || connectedProfileName?.srcpracticedname
    || 'No connected profile';
  const selectedOfficeSra = officeOnlineSraMembers.find((sra) => sra.SraId === selectedOfficeSraId);
  const selectedOfficeSraLabel = selectedOfficeSra ? resolveSraMemberLabel(selectedOfficeSra) : '—';
  const radioSendOptions = useMemo(
    () => sendAssessmentOptions.filter((option) => !isCopyPasteOption(option)),
    [sendAssessmentOptions]
  );
  const copyPasteOption = useMemo(
    () => sendAssessmentOptions.find((option) => isCopyPasteOption(option)),
    [sendAssessmentOptions]
  );
  const copyPasteOptionKey = copyPasteOption ? getSendMethodOptionKey(copyPasteOption) : '';
  // Copy/paste is now just another mutually-exclusive "Send Assessment By" choice (rendered as
  // its own radio row — see renderClientInfoContent) rather than an independently-toggled
  // checkbox, so "selected" is derived from sendMethod like every other option instead of its
  // own boolean. copypaste (PHI_FIELD.COPYPASTE) is still kept in sync on every selection via
  // selectSendMethod below — it's a real persisted PHI field other code may read.
  const copyPasteSelected = !!copyPasteOptionKey && sendMethod === copyPasteOptionKey;
  const selectedAssessmentPla = assessmentOptions.find((pla) => getPlaKey(pla) === selectedAssessmentKey);
  const selectedAssessmentLabel = selectedAssessmentPla ? resolvePlaLabel(selectedAssessmentPla) : '—';
  const selectedAssessmentId = selectedAssessmentPla?.AssessmentId;
  const matchingLaunch = selectedAssessmentId
    ? [...(profile.launches ?? [])].reverse().find((launch) => launch.AssessmentId === selectedAssessmentId)
    : undefined;
  const secureLink = matchingLaunch?.SSKey
    ? `${resolveCurrentBaseUrl()}/secure/${matchingLaunch.SSKey}`
    : '';
  const showAdditionalPhi = enableFlags.enable_phimgmt && additionalPhiFields.length > 0;
  const sectionLabels = useMemo<EditSectionLabel[]>(() => {
    const labels: EditSectionLabel[] = ['Client Info'];

    if (enableFlags.enable_schedulemgmt && (enableFlags.enable_manualscheduling || enableFlags.enable_touchpointscheduling)) {
      labels.push('Schedule');
    }
    if (enableFlags.enable_patrefmgmt) {
      labels.push('Flags');
    }
    if (enableFlags.enable_medinfomgmt) {
      labels.push('Extended Info');
    }
    if (enableFlags.enable_patconnmgmt) {
      labels.push('Connections');
    }
    if (enableFlags.enable_messagedirect) {
      labels.push('Messages');
    }

    labels.push('Reports');
    return labels;
  }, [
    enableFlags.enable_patrefmgmt,
    enableFlags.enable_medinfomgmt,
    enableFlags.enable_messagedirect,
    enableFlags.enable_patconnmgmt,
    enableFlags.enable_schedulemgmt,
    enableFlags.enable_manualscheduling,
    enableFlags.enable_touchpointscheduling,
  ]);

  // Selects a "Send Assessment By" option (Email/Text/App/Local/Link) — the single source of
  // mutual exclusion is sendMethod (form.howcontact) itself. copypaste (PHI_FIELD.COPYPASTE) is
  // still a real persisted PHI field other code may read, so it's kept in sync here rather than
  // left stuck at whatever it was last set to.
  const selectSendMethod = (optionKey: string, isCopyPaste: boolean = false) => {
    setField('howcontact', optionKey);
    setField('copypaste', isCopyPaste ? '1' : '0');
  };

  const cycleOfficeSra = () => {
    if (officeOnlineSraMembers.length === 0) return;
    const idx = officeOnlineSraMembers.findIndex((sra) => sra.SraId === selectedOfficeSraId);
    const next = idx < 0 || idx === officeOnlineSraMembers.length - 1
      ? officeOnlineSraMembers[0]
      : officeOnlineSraMembers[idx + 1];
    setField('localsra', next.SraId);
  };

  const cycleAssessmentSelection = () => {
    if (assessmentOptions.length === 0) return;
    const idx = assessmentOptions.findIndex((pla) => getPlaKey(pla) === selectedAssessmentKey);
    const next = idx < 0 || idx === assessmentOptions.length - 1
      ? assessmentOptions[0]
      : assessmentOptions[idx + 1];
    setSelectedAssessmentKey(getPlaKey(next));
  };

  useEffect(() => {
    const mapped = profileToClientEditForm(profile);

    setForm((current) => {
      const needsFname = !current.fname && !!mapped.fname;
      const needsLname = !current.lname && !!mapped.lname;
      if (!needsFname && !needsLname) {
        return current;
      }

      return {
        ...current,
        fname: needsFname ? mapped.fname : current.fname,
        lname: needsLname ? mapped.lname : current.lname,
      };
    });
  }, [profile]);

  useEffect(() => {
    let cancelled = false;

    if (toggleArrowIcons.loaded) {
      return () => {
        cancelled = true;
      };
    }

    void loadToggleArrowIcons().then((icons) => {
      if (!cancelled) {
        setToggleArrowIcons(icons);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [toggleArrowIcons.loaded]);

  useEffect(() => {
    let cancelled = false;

    if (pdfIcon.loaded) {
      return () => {
        cancelled = true;
      };
    }

    void loadPdfIcon().then((icon) => {
      if (!cancelled) {
        setPdfIcon(icon);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [pdfIcon.loaded]);

  useEffect(() => {
    let cancelled = false;
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;

    const syncFromCache = () => {
      void loadAssessmentsBySourceId().then((map) => {
        if (!cancelled) {
          setAssessmentsBySourceId(map);
        }
      });
    };

    // Initial load — skip the storage read if the shared module cache is already warm.
    if (!getCachedAssessmentsBySourceId()) {
      syncFromCache();
    }

    // The assessment-definition list is written after these rows may already have mounted —
    // cold-start bundle unpack, a debug manual download, an admin-result save. ClientReport-
    // Assessments' own listener (registered at import, so it runs before this one) has already
    // cleared the module cache by the time this fires, so a plain re-read picks up the new
    // data; the delay coalesces the burst of per-assessment writes into a single reload.
    const handleContentUpdated = () => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
      refreshTimer = setTimeout(syncFromCache, 250);
    };
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener(ASSESSMENT_CONTENT_UPDATED_EVENT, handleContentUpdated);
    }

    return () => {
      cancelled = true;
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
      if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
        window.removeEventListener(ASSESSMENT_CONTENT_UPDATED_EVENT, handleContentUpdated);
      }
    };
  }, []);

  // Self-heal: if this profile has history rows pointing at an assessment the loaded map doesn't
  // know about, force one re-read. Covers the odd case where the content-updated event fired
  // before this row's listener was attached, or the list was written by a path that didn't emit
  // it. Capped so a genuinely-missing assessment (not in storage at all) can't loop forever.
  const assessmentHealAttemptsRef = useRef(0);
  useEffect(() => {
    const historyItems = profile.history ?? [];
    if (historyItems.length === 0 || assessmentHealAttemptsRef.current >= 2) {
      return;
    }
    const hasUnresolved = historyItems.some(
      (h) => h.Admin?.RecAssessment && !assessmentsBySourceId.has(h.Admin.RecAssessment)
    );
    if (!hasUnresolved) {
      return;
    }
    assessmentHealAttemptsRef.current += 1;
    let cancelled = false;
    void loadAssessmentsBySourceId(true).then((map) => {
      if (!cancelled) {
        setAssessmentsBySourceId(map);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [profile.history, assessmentsBySourceId]);

  useEffect(() => {
    if (sendMethod) return;

    if (radioSendOptions.length > 0) {
      setField('howcontact', getSendMethodOptionKey(radioSendOptions[0]));
    } else if (showLocalSendOption) {
      setField('howcontact', 'Local');
    }
  }, [radioSendOptions, showLocalSendOption, sendMethod]);

  useEffect(() => {
    if (selectedOfficeSraId) return;
    if (officeOnlineSraMembers.length > 0) {
      setField('localsra', officeOnlineSraMembers[0].SraId);
    }
  }, [officeOnlineSraMembers, selectedOfficeSraId]);

  useEffect(() => {
    if (selectedAssessmentKey) return;
    if (assessmentOptions.length === 0) return;

    // Prefer whichever assessment this profile actually has a launch for — defaulting to
    // assessmentOptions[0] regardless of the profile meant a client launched for their 2nd/3rd
    // configured assessment loaded with the dropdown defaulted to the WRONG one, so
    // matchingLaunch (Client Info's Request Status / Send Assessment Link section) found no
    // match even though a launch existed. The summary row's "Next" column isn't affected by
    // this since it reads profile.launches directly, not the selected dropdown assessment —
    // which is exactly why the mismatch showed a due date there but "no launch" in Client Info.
    const launchedAssessmentIds = new Set(
      (profile.launches ?? []).map((launch) => launch.AssessmentId).filter(Boolean)
    );
    const launchedOption = assessmentOptions.find(
      (pla) => pla.AssessmentId && launchedAssessmentIds.has(pla.AssessmentId)
    );
    setSelectedAssessmentKey(getPlaKey(launchedOption ?? assessmentOptions[0]));
  }, [assessmentOptions, selectedAssessmentKey, profile.launches]);

  useEffect(() => {
    setForm((current) => {
      const missingNames = additionalPhiFields
        .map((field) => field.Name)
        .filter((name) => !(name in current.extraPhi));
      if (missingNames.length === 0) {
        return current;
      }

      const nextExtra = { ...current.extraPhi };
      missingNames.forEach((name) => {
        nextExtra[name] = getPhiValue(profile.phi ?? [], name);
      });
      return { ...current, extraPhi: nextExtra };
    });
  }, [additionalPhiFields, profile]);

  useEffect(() => {
    if (isWeb) return;

    try {
      const datePickerModule = require('@react-native-community/datetimepicker');
      const pickerComponent = (datePickerModule?.default ?? datePickerModule) as MobileDateTimePickerType | undefined;
      if (pickerComponent) {
        setMobileDateTimePicker(() => pickerComponent);
      }
    } catch (error) {
      console.warn('ProfileRowEditor: mobile datetime picker unavailable; using text fallback', error);
    }
  }, [isWeb]);

  const activeArrowSrc = expanded ? toggleArrowIcons.upSrc : toggleArrowIcons.downSrc;

  const renderPdfIconGlyph = () => (
    pdfIcon.src ? (
      <Image source={{ uri: pdfIcon.src }} style={styles.pdfIconImage} resizeMode="contain" />
    ) : (
      <Text style={styles.reportLinkIcon}>📄</Text>
    )
  );

  // "View report inline" — switches the current tab to the full ReportView UI (see
  // ViewReportParams.mode) rather than generating a PDF, so a distinct glyph from the PDF
  // icon avoids implying the same "opens a new tab" behavior. A lined-page glyph (not the
  // eye emoji originally used here — reported as "creepy") in the same visual family as the
  // 📋 clipboard icon used for "copy secure link" above.
  const renderInlineViewIconGlyph = () => (
    <Text style={styles.reportLinkIcon}>📃</Text>
  );

  const resolveAssessmentForHistoryItem = (historyItem: PatinfoHistoryDto): AdminAssessmentDto | null => {
    return resolveHistoryItemReport(historyItem, assessmentsBySourceId).assessmentDefinition;
  };

  const resolveSelectedReport = (
    historyItem: PatinfoHistoryDto,
    assessmentDefinition: AdminAssessmentDto | null
  ): AdminAssessmentReportDto | null => {
    const reports = assessmentDefinition?.reports ?? [];
    if (reports.length === 0) {
      return null;
    }
    const selectedReportId = selectedReportIdByRecId[historyItem.Admin.RecId];
    if (selectedReportId) {
      const explicit = reports.find((r) => r.reportId === selectedReportId);
      if (explicit) {
        return explicit;
      }
    }
    // No explicit user pick yet (or a stale one that no longer matches a report) — fall back
    // to the account's configured "Default Report" (Account > Assessment Options) before the
    // plain reports[0] fallback, so the profile header row's View/PDF icons and this table's
    // own Report-selection dropdown initial value both open the report the account actually
    // wants by default, not just whichever one happens to be listed first.
    const defaultReportId = resolveDefaultReportId(assessmentDefinition, assessmentOptions);
    return reports.find((r) => r.reportId === defaultReportId) ?? reports[0];
  };

  const cycleSelectedReport = (historyItem: PatinfoHistoryDto, reports: AdminAssessmentReportDto[]) => {
    if (reports.length === 0) {
      return;
    }
    const recId = historyItem.Admin.RecId;
    const currentReportId = selectedReportIdByRecId[recId] ?? reports[0].reportId;
    const idx = reports.findIndex((r) => r.reportId === currentReportId);
    const next = idx < 0 || idx === reports.length - 1 ? reports[0] : reports[idx + 1];
    setSelectedReportIdByRecId((prev: Record<string, string>) => ({ ...prev, [recId]: next.reportId }));
  };

  const handleViewReport = (historyItem: PatinfoHistoryDto, mode: 'pdf' | 'inline' = 'pdf') => {
    if (!historyItem?.Admin?.RecId || !onViewReport) {
      return;
    }
    const assessmentDefinition = resolveAssessmentForHistoryItem(historyItem);
    const selectedReport = resolveSelectedReport(historyItem, assessmentDefinition);
    onViewReport({
      profile,
      adminResponse: normalizeHistoryAdminResponse(historyItem.Admin),
      assessmentDefinition,
      selectedReport,
      mode,
    });
  };

  const toggleExpand = () => {
    setExpanded(e => !e);
    setSaveError(null);
    setSendNowError(null);
    setSendNowOk(false);
    setCancelError(null);
    setCopyFeedback(null);
  };

  const setField = (field: keyof ClientProfileEditForm, value: string) => {
    setForm(f => ({ ...f, [field]: value }));
  };

  const setExtraPhiField = (name: string, value: string) => {
    setForm(f => ({ ...f, extraPhi: { ...f.extraPhi, [name]: value } }));
  };

  const handleSave = async () => {
    const payload = buildClientProfilePutPayload(profile, form, {
      touchpointSchedules,
      selectedScheduleIds,
      scheduleStartDates,
    });
    if (!payload) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      await onSave(payload);
    } catch (err: any) {
      setSaveError(err?.message ?? 'Failed to save. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const canSendNow = !!selectedAssessmentPla && !!sendMethod;

  // Send Now/Schedule both deliver via the selected How Contact method — Email needs a
  // plausible email address, Text needs a plausible phone number. Copy/Paste bypasses both:
  // it's a valid delivery on its own (the link is handed to the client directly), so it
  // shouldn't be blocked on contact info the client may not have on file.
  const resolveSendValidationError = (): string | null => {
    if (copyPasteSelected) {
      return null;
    }
    if (isEmailHowContact && !isValidEmailAddress(form.email)) {
      return 'Enter a valid email address to send by Email.';
    }
    if (isTextHowContact && !isValidPhoneNumber(form.phone)) {
      return 'Enter a valid phone number to send by Text.';
    }
    return null;
  };

  const handleSendNow = async () => {
    if (!selectedAssessmentPla || !sendMethod) {
      return;
    }

    const validationError = resolveSendValidationError();
    if (validationError) {
      setSendNowError(validationError);
      setSendNowOk(false);
      return;
    }

    setIsSendingNow(true);
    setSendNowError(null);
    setSendNowOk(false);
    try {
      const request = buildSendNowLaunchRequest(profile, form, selectedAssessmentPla, isTextHowContact);
      await onSendNow(profile, request);
      setSendNowOk(true);
    } catch (err: any) {
      setSendNowError(err?.message ?? 'Failed to send. Please try again.');
    } finally {
      setIsSendingNow(false);
    }
  };

  const canSchedule = !!selectedAssessmentPla && !!dueDate && !matchingLaunch;

  const handleSchedule = async () => {
    if (!selectedAssessmentPla || !dueDate || matchingLaunch) {
      return;
    }

    const validationError = resolveSendValidationError();
    if (validationError) {
      setScheduleError(validationError);
      return;
    }

    setIsScheduling(true);
    setScheduleError(null);
    try {
      // Same launch_secure_put action as Send Now, just with a manually-entered due date
      // and the Pla's full NotifySched reminder list instead of a single force-send event.
      const request = buildScheduleLaunchRequest(profile, form, selectedAssessmentPla, dueDate, isTextHowContact);
      await onSendNow(profile, request);
    } catch (err: any) {
      setScheduleError(err?.message ?? 'Failed to schedule. Please try again.');
    } finally {
      setIsScheduling(false);
    }
  };

  const handleCancelLaunch = async () => {
    if (!matchingLaunch) {
      return;
    }

    setIsCancelling(true);
    setCancelError(null);
    try {
      const request = buildCancelLaunchRequest(matchingLaunch);
      await onCancelLaunch(profile, request);
    } catch (err: any) {
      setCancelError(err?.message ?? 'Failed to cancel. Please try again.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCopySecureLink = async () => {
    if (!secureLink) {
      return;
    }

    const copied = await copySecureLinkToClipboard(secureLink);
    // No success label ("Link copied" wrapped and clipped at the column edge) — only a failure
    // still surfaces, since a silent failed copy would leave the user pasting stale clipboard text.
    setCopyFeedback(copied ? null : 'Failed to copy link');
  };

  const handleMobileDueDateChange = (_event: any, selectedDate?: Date) => {
    setShowMobileDueDatePicker(false);
    if (selectedDate) {
      setDueDate(selectedDate.toISOString().slice(0, 10));
    }
  };

  const renderClientInfoContent = () => {
    return (
      <View style={styles.clientInfoGrid}>
        <View style={[styles.clientInfoPrimaryCell, isNarrow && styles.clientInfoCellNarrow]}>
          <Text style={styles.clientInfoHeader}>Send Assessment By</Text>

          {radioSendOptions.map((option) => {
            const optionKey = getSendMethodOptionKey(option);
            const optionKind = resolveSendMethodOptionKind(option.AText);
            return (
              <View style={styles.sendMethodRow} key={optionKey || option.AText}>
                <TouchableOpacity
                  style={styles.radioBtn}
                  onPress={() => selectSendMethod(optionKey)}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: sendMethod === optionKey }}
                >
                  {sendMethod === optionKey ? <View style={styles.radioDot} /> : null}
                </TouchableOpacity>
                <Text style={styles.sendMethodLabel}>{option.AText}</Text>
                {optionKind === 'email' ? (
                  <TextInput
                    style={styles.sendMethodInput}
                    value={form.email}
                    onChangeText={v => setField('email', v)}
                    placeholder="Email"
                    placeholderTextColor={EDITOR_COLORS.placeholder}
                    autoCapitalize="none"
                    keyboardType="email-address"
                  />
                ) : optionKind === 'app' ? (
                  <Text style={styles.sendMethodStaticValue}>{connectedProfileDisplay}</Text>
                ) : optionKind === 'phone' ? (
                  <TextInput
                    style={styles.sendMethodInput}
                    value={form.phone}
                    onChangeText={v => setField('phone', v)}
                    placeholder="Phone number"
                    placeholderTextColor={EDITOR_COLORS.placeholder}
                    keyboardType="phone-pad"
                  />
                ) : null}
              </View>
            );
          })}

          {showLocalSendOption ? (
            <View style={styles.sendMethodRow}>
              <TouchableOpacity
                style={styles.radioBtn}
                onPress={() => selectSendMethod('Local')}
                accessibilityRole="radio"
                accessibilityState={{ selected: sendMethod === 'Local' }}
              >
                {sendMethod === 'Local' ? <View style={styles.radioDot} /> : null}
              </TouchableOpacity>
              <Text style={styles.sendMethodLabel}>Local</Text>
              {isWeb ? (
                <select
                  style={webInlineControlStyle}
                  value={selectedOfficeSraId}
                  onChange={(event: any) => setField('localsra', String(event?.target?.value ?? ''))}
                >
                  {officeOnlineSraMembers.map((sra) => (
                    <option key={sra.SraId} value={sra.SraId}>
                      {resolveSraMemberLabel(sra)}
                    </option>
                  ))}
                </select>
              ) : (
                <TouchableOpacity
                  style={styles.dropdownInlineBtn}
                  onPress={cycleOfficeSra}
                >
                  <Text style={styles.dropdownInlineText}>{selectedOfficeSraLabel}</Text>
                  <Text style={styles.dropdownInlineChevron}>▼</Text>
                </TouchableOpacity>
              )}
            </View>
          ) : null}

          {copyPasteOption ? (
            <View style={styles.sendMethodRow}>
              <TouchableOpacity
                style={styles.radioBtn}
                onPress={() => selectSendMethod(copyPasteOptionKey, true)}
                accessibilityRole="radio"
                accessibilityState={{ selected: copyPasteSelected }}
                accessibilityLabel={COPY_PASTE_LINK_LABEL}
              >
                {copyPasteSelected ? <View style={styles.radioDot} /> : null}
              </TouchableOpacity>
              <Text style={styles.sendMethodLabel}>Link</Text>
              {matchingLaunch && secureLink ? (
                <View style={styles.secureLinkRow}>
                  {isWeb ? (
                    React.createElement(
                      'a',
                      {
                        href: secureLink,
                        target: '_blank',
                        rel: 'noopener noreferrer',
                        style: styles.secureLinkText,
                      },
                      'Assessment Link'
                    )
                  ) : (
                    <TouchableOpacity
                      style={styles.secureLinkTextWrap}
                      onPress={() => { void Linking.openURL(secureLink); }}
                      accessibilityRole="link"
                    >
                      <Text style={styles.secureLinkText} numberOfLines={1}>Assessment Link</Text>
                    </TouchableOpacity>
                  )}

                  <TouchableOpacity
                    style={styles.copyIconBtn}
                    onPress={() => { void handleCopySecureLink(); }}
                    accessibilityRole="button"
                    accessibilityLabel="Copy secure link"
                  >
                    <Text style={styles.copyIconText}>📋</Text>
                  </TouchableOpacity>

                  {copyFeedback ? (
                    <Text style={styles.copyFeedbackText}>{copyFeedback}</Text>
                  ) : null}
                </View>
              ) : (
                <Text style={styles.sendMethodStaticValue}>Copy/Paste Link</Text>
              )}
            </View>
          ) : null}

          <View style={styles.sendMethodSpacerRow} />

          {assessmentOptions.length > 1 ? (
            <View style={styles.sendMethodRow}>
              <Text style={styles.sendMethodLabelWide}>Assessment</Text>
              {isWeb ? (
                <select
                  style={webInlineControlStyle}
                  value={selectedAssessmentKey}
                  onChange={(event: any) => setSelectedAssessmentKey(String(event?.target?.value ?? ''))}
                  disabled={!!matchingLaunch}
                >
                  {assessmentOptions.map((pla) => (
                    <option key={getPlaKey(pla)} value={getPlaKey(pla)}>
                      {resolvePlaLabel(pla)}
                    </option>
                  ))}
                </select>
              ) : (
                <TouchableOpacity
                  style={[styles.dropdownInlineBtn, !!matchingLaunch && styles.dropdownInlineBtnDisabled]}
                  onPress={matchingLaunch ? undefined : cycleAssessmentSelection}
                  disabled={!!matchingLaunch}
                >
                  <Text style={styles.dropdownInlineText}>{selectedAssessmentLabel}</Text>
                  <Text style={styles.dropdownInlineChevron}>▼</Text>
                </TouchableOpacity>
              )}
            </View>
          ) : null}

          {matchingLaunch ? (
            <>
              <Text style={[styles.clientInfoHeader, styles.requestStatusHeader]}>Request Status</Text>
              <View style={styles.notifyStatusList}>
                {(matchingLaunch.NotifyState ?? []).map((event, index) => (
                  <View style={styles.notifyStatusRow} key={`${event.Type}-${index}`}>
                    <View style={styles.notifyStatusDateCol}>
                      <Text style={styles.notifyStatusValue}>{event.EDate ? formatDate(event.EDate) : ''}</Text>
                    </View>
                    <View style={styles.notifyStatusTypeCol}>
                      <Text style={styles.notifyStatusType}>{resolveNotifyTypeLabel(event.Type)}</Text>
                    </View>
                    <View style={styles.notifyStatusSentCol}>
                      <Text style={styles.notifyStatusValue} numberOfLines={1}>{event.Sent}</Text>
                    </View>
                  </View>
                ))}
              </View>

              {cancelError ? (
                <Text style={styles.sendNowErrorText}>{cancelError}</Text>
              ) : null}

              <View style={styles.cancelRow}>
                <TouchableOpacity
                  style={[styles.cancelBtn, isCancelling && styles.sendNowBtnDisabled]}
                  onPress={() => { void handleCancelLaunch(); }}
                  disabled={isCancelling}
                >
                  {isCancelling ? (
                    <ActivityIndicator size="small" color={EDITOR_COLORS.submitText} />
                  ) : (
                    <Text style={styles.minimalSubmitBtnText}>Cancel</Text>
                  )}
                </TouchableOpacity>
              </View>
            </>
          ) : (
            <>
              {sendNowError ? (
                <Text style={styles.sendNowErrorText}>{sendNowError}</Text>
              ) : null}
              <View style={styles.sendNowRow}>
                <TouchableOpacity
                  style={[styles.sendNowBtn, (isSendingNow || !canSendNow) && styles.sendNowBtnDisabled]}
                  onPress={() => { void handleSendNow(); }}
                  disabled={isSendingNow || !canSendNow}
                >
                  {isSendingNow ? (
                    <ActivityIndicator size="small" color={EDITOR_COLORS.actionText} />
                  ) : (
                    <Text style={styles.sendNowBtnText}>Send Assessment Link</Text>
                  )}
                </TouchableOpacity>
              </View>
            </>
          )}


        </View>

        <View style={[styles.clientInfoSecondaryCell, isNarrow && styles.clientInfoCellNarrow]}>
          {showAdditionalPhi ? (
            <>
              <Text style={styles.clientInfoHeader}> </Text>

              {additionalPhiFields.map((field) => {
                const normalizedName = normalizePhiFieldName(field.Name);
                const label = resolvePhiFieldLabel(field);
                const rowKey = field.MVarId || field.Name;
                const fieldValue = form.extraPhi[field.Name] ?? '';

                if (normalizedName === 'dob') {
                  return (
                    <View style={styles.phiRow} key={rowKey}>
                      <Text style={styles.phiName}>{label}</Text>
                      {isWeb ? (
                        React.createElement('input' as any, {
                          type: 'date',
                          value: fieldValue,
                          onChange: (event: any) => setExtraPhiField(field.Name, String(event?.target?.value ?? '')),
                          style: webInlineControlStyle,
                          'aria-label': label,
                        })
                      ) : (
                        MobileDateTimePicker ? (
                          <>
                            <TouchableOpacity
                              style={styles.mobileDateInputBtn}
                              onPress={() => setActiveMobileDobField(field.Name)}
                              accessibilityRole="button"
                              accessibilityLabel={`Select ${label}`}
                            >
                              <Text style={[styles.mobileDateInputText, !fieldValue && styles.mobileDateInputPlaceholder]}>
                                {fieldValue || 'YYYY-MM-DD'}
                              </Text>
                            </TouchableOpacity>

                            {activeMobileDobField === field.Name ? (
                              <MobileDateTimePicker
                                value={fieldValue ? new Date(`${fieldValue}T00:00:00`) : new Date()}
                                mode="date"
                                display="default"
                                onChange={(_event: any, selectedDate?: Date) => {
                                  setActiveMobileDobField(null);
                                  if (selectedDate) {
                                    setExtraPhiField(field.Name, selectedDate.toISOString().slice(0, 10));
                                  }
                                }}
                              />
                            ) : null}
                          </>
                        ) : (
                          <TextInput
                            style={styles.phiValueInput}
                            value={fieldValue}
                            onChangeText={v => setExtraPhiField(field.Name, v)}
                            placeholder="YYYY-MM-DD"
                            placeholderTextColor={EDITOR_COLORS.placeholder}
                            maxLength={10}
                          />
                        )
                      )}
                    </View>
                  );
                }

                if (isGenderFieldName(field.Name)) {
                  const selectedGenderOption = genderOptions.find(
                    (option) => getSendMethodOptionKey(option) === fieldValue
                  );
                  const selectedGenderLabel = selectedGenderOption ? selectedGenderOption.AText : '—';
                  const cycleGenderSelection = () => {
                    if (genderOptions.length === 0) return;
                    const idx = genderOptions.findIndex((option) => getSendMethodOptionKey(option) === fieldValue);
                    const next = idx < 0 || idx === genderOptions.length - 1
                      ? genderOptions[0]
                      : genderOptions[idx + 1];
                    setExtraPhiField(field.Name, getSendMethodOptionKey(next));
                  };

                  return (
                    <View style={styles.phiRow} key={rowKey}>
                      <Text style={styles.phiName}>{label}</Text>
                      {isWeb ? (
                        <select
                          style={webInlineControlStyle}
                          value={fieldValue}
                          onChange={(event: any) => setExtraPhiField(field.Name, String(event?.target?.value ?? ''))}
                        >
                          {genderOptions.map((option) => (
                            <option key={getSendMethodOptionKey(option)} value={getSendMethodOptionKey(option)}>
                              {option.AText}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <TouchableOpacity
                          style={styles.dropdownInlineBtn}
                          onPress={cycleGenderSelection}
                        >
                          <Text style={styles.dropdownInlineText}>{selectedGenderLabel}</Text>
                          <Text style={styles.dropdownInlineChevron}>▼</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  );
                }

                return (
                  <View style={styles.phiRow} key={rowKey}>
                    <Text style={styles.phiName}>{label}</Text>
                    <TextInput
                      style={styles.phiValueInput}
                      value={fieldValue}
                      onChangeText={v => setExtraPhiField(field.Name, v)}
                      placeholder={label}
                      placeholderTextColor={EDITOR_COLORS.placeholder}
                    />
                  </View>
                );
              })}
            </>
          ) : null}

          <TouchableOpacity
            style={[
              styles.minimalSubmitBtn,
              !showAdditionalPhi && styles.minimalSubmitBtnTight,
              isSaving && styles.minimalSubmitBtnDisabled,
            ]}
            onPress={() => { void handleSave(); }}
            disabled={isSaving}
          >
            {isSaving ? (
              <ActivityIndicator size="small" color={EDITOR_COLORS.submitText} />
            ) : (
              <Text style={styles.minimalSubmitBtnText}>Update</Text>
            )}
          </TouchableOpacity>

        </View>
      </View>
    );
  };

  const toggleScheduleSelected = (scheduleKey: string) => {
    setSelectedScheduleIds((current) => ({ ...current, [scheduleKey]: !current[scheduleKey] }));
  };

  const renderDateField = (
    value: string,
    onChangeValue: (value: string) => void,
    ariaLabel: string,
    isActiveMobilePicker: boolean,
    onOpenMobilePicker: () => void,
    onCloseMobilePicker: () => void
  ) => {
    if (isWeb) {
      return React.createElement('input' as any, {
        type: 'date',
        value,
        onChange: (event: any) => onChangeValue(String(event?.target?.value ?? '')),
        style: webInlineControlStyle,
        'aria-label': ariaLabel,
      });
    }

    if (MobileDateTimePicker) {
      return (
        <>
          <TouchableOpacity
            style={styles.mobileDateInputBtn}
            onPress={onOpenMobilePicker}
            accessibilityRole="button"
            accessibilityLabel={`Select ${ariaLabel}`}
          >
            <Text style={[styles.mobileDateInputText, !value && styles.mobileDateInputPlaceholder]}>
              {value || 'YYYY-MM-DD'}
            </Text>
          </TouchableOpacity>

          {isActiveMobilePicker ? (
            <MobileDateTimePicker
              value={value ? new Date(`${value}T00:00:00`) : new Date()}
              mode="date"
              display="default"
              onChange={(_event: any, selectedDate?: Date) => {
                onCloseMobilePicker();
                if (selectedDate) {
                  onChangeValue(selectedDate.toISOString().slice(0, 10));
                }
              }}
            />
          ) : null}
        </>
      );
    }

    return (
      <TextInput
        style={styles.sendMethodInput}
        value={value}
        onChangeText={onChangeValue}
        placeholder="YYYY-MM-DD"
      />
    );
  };

  const renderScheduleContent = () => {
    const showManualColumn = enableFlags.enable_manualscheduling;
    const showTouchpointColumn = enableFlags.enable_touchpointscheduling;
    // The Pla currently selected for launch (Client Info section) already has an active
    // launch — manual scheduling doesn't apply again until it's cancelled.
    const scheduleDisabledByLaunch = !!matchingLaunch;

    const manualColumn = showManualColumn ? (
      <View style={[styles.scheduleManualCell, isNarrow && styles.clientInfoCellNarrow]}>
        {scheduleDisabledByLaunch ? (
          <View style={styles.sendMethodRow}>
            <Text style={styles.requestInProcessText}>Request in process</Text>
          </View>
        ) : (
          <>
            <View style={styles.sendMethodRow}>
              <Text style={styles.sendMethodLabelWide}>Due Date</Text>
              {isWeb ? (
                React.createElement('input' as any, {
                  type: 'date',
                  value: dueDate,
                  onChange: (event: any) => setDueDate(String(event?.target?.value ?? '')),
                  style: webInlineControlStyle,
                  'aria-label': 'Due date',
                })
              ) : (
                MobileDateTimePicker ? (
                  <>
                    <TouchableOpacity
                      style={styles.mobileDateInputBtn}
                      onPress={() => setShowMobileDueDatePicker(true)}
                      accessibilityRole="button"
                      accessibilityLabel="Select due date"
                    >
                      <Text style={[styles.mobileDateInputText, !dueDate && styles.mobileDateInputPlaceholder]}>
                        {dueDate || 'YYYY-MM-DD'}
                      </Text>
                    </TouchableOpacity>

                    {showMobileDueDatePicker ? (
                      <MobileDateTimePicker
                        value={dueDate ? new Date(`${dueDate}T00:00:00`) : new Date()}
                        mode="date"
                        display="default"
                        onChange={handleMobileDueDateChange}
                      />
                    ) : null}
                  </>
                ) : (
                  <TextInput
                    style={styles.sendMethodInput}
                    value={dueDate}
                    onChangeText={setDueDate}
                    placeholder="YYYY-MM-DD"
                  />
                )
              )}
            </View>

            {scheduleError ? (
              <Text style={styles.sendNowErrorText}>{scheduleError}</Text>
            ) : null}

            <View style={styles.sendMethodRow}>
              <Text style={styles.sendMethodLabelWide} />
              <TouchableOpacity
                style={[styles.minimalSubmitBtn, (isScheduling || !canSchedule) && styles.minimalSubmitBtnDisabled]}
                onPress={() => { void handleSchedule(); }}
                disabled={isScheduling || !canSchedule}
              >
                {isScheduling ? (
                  <ActivityIndicator size="small" color={EDITOR_COLORS.submitText} />
                ) : (
                  <Text style={styles.minimalSubmitBtnText}>Schedule</Text>
                )}
              </TouchableOpacity>
            </View>
          </>
        )}
      </View>
    ) : null;

    // Floats into the primary-cell position/styling when it's the only column shown
    // (manual scheduling disabled) rather than sitting in the narrower secondary cell.
    // When paired with the manual column, nudged right 50px to better align with the
    // Client Info section's second column above it.
    const touchpointColumn = showTouchpointColumn ? (
      <View style={[
        showManualColumn ? styles.clientInfoSecondaryCell : styles.clientInfoPrimaryCell,
        showManualColumn && !isNarrow && styles.touchpointColumnShift,
        isNarrow && styles.clientInfoCellNarrow,
      ]}>
        <Text style={styles.clientInfoHeader}>Enable Touchpoint Schedules</Text>

        {touchpointSchedules.length === 0 ? (
          <Text style={styles.editTablePlaceholder}>No schedules available</Text>
        ) : (
          touchpointSchedules.map((schedule) => {
            const scheduleKey = normalizeScheduleKey(schedule.registryId) || normalizeScheduleKey(schedule.name);
            const checked = !!selectedScheduleIds[scheduleKey];
            const showStartDate = checked && schedule.type === SCHEDULE_TYPE_MONITOR;
            const startDateValue = scheduleStartDates[scheduleKey] ?? '';

            return (
              <React.Fragment key={scheduleKey}>
                <View style={styles.touchpointCheckboxRow}>
                  {isWeb ? (
                    React.createElement('input', {
                      type: 'checkbox',
                      checked,
                      onChange: () => toggleScheduleSelected(scheduleKey),
                      style: webCheckboxStyle,
                      'aria-label': schedule.name,
                    })
                  ) : (
                    <TouchableOpacity
                      style={styles.checkboxBtn}
                      onPress={() => toggleScheduleSelected(scheduleKey)}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked }}
                    >
                      {checked ? <Text style={styles.checkboxMark}>✓</Text> : null}
                    </TouchableOpacity>
                  )}
                  <Text style={styles.sendMethodStaticValue}>{schedule.name}</Text>
                </View>

                {showStartDate ? (
                  <View style={[styles.sendMethodRow, styles.scheduleStartDateRow]}>
                    <Text style={styles.sendMethodLabelWide}>Start Date</Text>
                    {renderDateField(
                      startDateValue,
                      (value) => setScheduleStartDates((current) => ({ ...current, [scheduleKey]: value })),
                      `${schedule.name} start date`,
                      activeMobileScheduleDateKey === scheduleKey,
                      () => setActiveMobileScheduleDateKey(scheduleKey),
                      () => setActiveMobileScheduleDateKey(null)
                    )}
                  </View>
                ) : null}
              </React.Fragment>
            );
          })
        )}
      </View>
    ) : null;

    return (
      <View>
        <View style={styles.clientInfoGrid}>
          {manualColumn}
          {touchpointColumn}
        </View>

        <View style={styles.scheduleUpdateRow}>
          <TouchableOpacity
            style={[styles.minimalSubmitBtn, styles.minimalSubmitBtnTight, isSaving && styles.minimalSubmitBtnDisabled]}
            onPress={() => { void handleSave(); }}
            disabled={isSaving}
          >
            {isSaving ? (
              <ActivityIndicator size="small" color={EDITOR_COLORS.submitText} />
            ) : (
              <Text style={styles.minimalSubmitBtnText}>Update</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const renderReportsContent = () => {
    const historyRows = sortHistoryByRecentFirst(profile.history ?? []);

    return (
      <View style={styles.reportsTable}>
        <View style={styles.reportsHeaderRow}>
          <Text style={[styles.reportsHeaderCell, styles.reportsColAssessment, isProAccount && styles.reportsColAssessmentWide]}>Assessment</Text>
          {isProAccount ? null : (
            <Text style={[styles.reportsHeaderCell, styles.reportsColLocation]}>Location</Text>
          )}
          <Text style={[styles.reportsHeaderCell, styles.reportsColCompleted, isProAccount && styles.reportsColCompletedWide]}>Completed</Text>
          <Text style={[styles.reportsHeaderCell, styles.reportsColReport, isProAccount && styles.reportsColReportWide]}>Report</Text>
          <Text style={[styles.reportsHeaderCell, styles.reportsColView, styles.reportsColViewHeader]} />
        </View>

        {historyRows.length === 0 ? (
          <View style={styles.reportsDataRow}>
            <Text style={styles.reportsEmptyText}>No reports available</Text>
          </View>
        ) : (
          historyRows.map((historyItem, index) => {
            const recId = historyItem.Admin?.RecId;
            const assessmentDefinition = resolveAssessmentForHistoryItem(historyItem);
            const reports = assessmentDefinition?.reports ?? [];
            const selectedReport = resolveSelectedReport(historyItem, assessmentDefinition);
            return (
              <View key={`${recId}-${index}`} style={styles.reportsDataRow}>
                <Text style={[styles.reportsDataCell, styles.reportsColAssessment, isProAccount && styles.reportsColAssessmentWide]}>{assessmentDefinition?.dName || '—'}</Text>
                {isProAccount ? null : (
                  <Text style={[styles.reportsDataCell, styles.reportsColLocation]}>{historyItem.PracDName || '—'}</Text>
                )}
                <Text style={[styles.reportsDataCell, styles.reportsColCompleted, isProAccount && styles.reportsColCompletedWide]}>{formatDateTime(historyItem.Admin?.RecUTM)}</Text>
                <View style={[styles.reportsColReport, isProAccount && styles.reportsColReportWide]}>
                  {reports.length > 0 ? (
                    isWeb ? (
                      <select
                        style={webInlineControlStyle}
                        value={selectedReport?.reportId ?? ''}
                        onChange={(event: any) => {
                          const reportId = String(event?.target?.value ?? '');
                          setSelectedReportIdByRecId((prev: Record<string, string>) => ({ ...prev, [recId]: reportId }));
                        }}
                      >
                        {reports.map((report) => (
                          <option key={report.reportId} value={report.reportId}>
                            {report.dName}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <TouchableOpacity
                        style={styles.dropdownInlineBtn}
                        onPress={() => cycleSelectedReport(historyItem, reports)}
                      >
                        <Text style={styles.dropdownInlineText} numberOfLines={1}>{selectedReport?.dName ?? ''}</Text>
                        <Text style={styles.dropdownInlineChevron}>▼</Text>
                      </TouchableOpacity>
                    )
                  ) : null}
                </View>
                <View style={[styles.reportsColView, styles.reportsColViewIcons]}>
                  {selectedReport ? (
                    <>
                      <TouchableOpacity
                        style={styles.reportLinkBtn}
                        onPress={() => handleViewReport(historyItem, 'inline')}
                        accessibilityRole="button"
                        accessibilityLabel={`View report ${recId} in ReportView`}
                      >
                        {renderInlineViewIconGlyph()}
                      </TouchableOpacity>
                      {enableReportPdf ? null : (
                        <TouchableOpacity
                          style={styles.reportLinkBtn}
                          onPress={() => handleViewReport(historyItem, 'pdf')}
                          accessibilityRole="button"
                          accessibilityLabel={`View report ${recId} as PDF`}
                        >
                          {renderPdfIconGlyph()}
                        </TouchableOpacity>
                      )}
                    </>
                  ) : (
                    <Text style={styles.reportsDataCell}>—</Text>
                  )}
                </View>
              </View>
            );
          })
        )}
      </View>
    );
  };

  return (
    <View style={[styles.row, expanded && styles.rowExpanded]}>
      <View style={styles.summaryRow}>
        <TouchableOpacity
          onPress={toggleExpand}
          style={styles.toggleCell}
          accessibilityRole="button"
          accessibilityLabel={expanded ? `Collapse ${displayName}` : `Expand ${displayName}`}
        >
          {activeArrowSrc ? (
            <Image source={{ uri: activeArrowSrc }} style={styles.toggleImage} resizeMode="contain" />
          ) : (
            <Text style={styles.toggleIcon}>{expanded ? '▲' : '▼'}</Text>
          )}
        </TouchableOpacity>

        <View style={styles.firstNameCell}>
          {expanded ? (
            <TextInput
              style={[styles.inlineInput, !isWeb && styles.inlineInputMobile]}
              value={String(form.fname ?? '')}
              onChangeText={v => setField('fname', v)}
              autoCapitalize="words"
              placeholder="First"
              placeholderTextColor={EDITOR_COLORS.placeholder}
            />
          ) : (
            <Text style={styles.cellValue}>{form.fname || '—'}</Text>
          )}
        </View>

        <View style={styles.lastNameCell}>
          {expanded ? (
            <TextInput
              style={[styles.inlineInput, !isWeb && styles.inlineInputMobile]}
              value={String(form.lname ?? '')}
              onChangeText={v => setField('lname', v)}
              autoCapitalize="words"
              placeholder="Last"
              placeholderTextColor={EDITOR_COLORS.placeholder}
            />
          ) : (
            <Text style={styles.cellValue}>{form.lname || '—'}</Text>
          )}
        </View>

        {enableFlags.enable_idmgmt ? (
          <View style={styles.idNumberCell}>
            {expanded ? (
              <View style={styles.idCellInner}>
                {isPatidEditable ? (
                  <TextInput
                    style={styles.inlineInput}
                    value={form.patid}
                    onChangeText={v => setField('patid', v)}
                    autoCapitalize="none"
                    placeholder="ID"
                    placeholderTextColor={EDITOR_COLORS.placeholder}
                  />
                ) : (
                  <Text style={styles.cellValue}>{idNumber}</Text>
                )}

                <TouchableOpacity
                  onPress={() => setIsPatidEditable(v => !v)}
                  style={styles.lockBtn}
                  accessibilityRole="button"
                  accessibilityLabel={isPatidEditable ? 'Lock ID field' : 'Unlock ID field'}
                >
                  <Text style={styles.lockBtnText}>{isPatidEditable ? '🔓' : '🔒'}</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <Text style={styles.cellValue}>{idNumber}</Text>
            )}
          </View>
        ) : null}

        <View style={enableFlags.enable_idmgmt ? styles.nextCell : styles.nextCellNoId}>
          <Text style={styles.cellValue}>{nextTouchpoint}</Text>
        </View>

        <View style={enableFlags.enable_idmgmt ? styles.lastReportCell : styles.lastReportCellNoId}>
          <Text style={styles.cellValue}>
            {summaryReportOverride
              ? formatDateTime(summaryReportOverride.testDate)
              : (lastReport ? formatDateTime(lastReport.Admin?.RecUTM) : '—')}
          </Text>
        </View>

        <View
          style={[
            enableFlags.enable_idmgmt ? styles.viewReportCell : styles.viewReportCellNoId,
            styles.viewReportCellIcons,
          ]}
        >
          {summaryReportOverride || lastReport ? (
            <>
              <TouchableOpacity
                onPress={() => (summaryReportOverride ? summaryReportOverride.onViewReport('inline') : handleViewReport(lastReport!, 'inline'))}
                accessibilityRole="link"
                accessibilityLabel={`View report inline for ${displayName}`}
                style={styles.reportLinkBtn}
              >
                {renderInlineViewIconGlyph()}
              </TouchableOpacity>
              {enableReportPdf ? null : (
                <TouchableOpacity
                  onPress={() => (summaryReportOverride ? summaryReportOverride.onViewReport('pdf') : handleViewReport(lastReport!, 'pdf'))}
                  accessibilityRole="link"
                  accessibilityLabel={`View report PDF for ${displayName}`}
                  style={styles.reportLinkBtn}
                >
                  {renderPdfIconGlyph()}
                </TouchableOpacity>
              )}
            </>
          ) : (
            <Text style={styles.cellValue}>—</Text>
          )}
        </View>
      </View>

      {expanded && (
        <View style={styles.editPanel}>
          <View style={styles.editTable}>
            {sectionLabels.map((section, index) => (
              <React.Fragment key={section}>
                <View
                  style={[
                    styles.editTableRow,
                    index === sectionLabels.length - 1 && styles.editTableRowLast,
                  ]}
                >
                  <View style={[styles.editTableLabelCell, isNarrow && styles.editTableLabelCellNarrow]}>
                    <Text style={[styles.editTableLabel, section === 'Schedule' && styles.editTableLabelScheduleOffset]}>
                      {section}
                    </Text>
                  </View>
                  <View style={styles.editTableContentCell}>
                    {section === 'Client Info' ? (
                      renderClientInfoContent()
                    ) : section === 'Schedule' ? (
                      renderScheduleContent()
                    ) : section === 'Reports' ? (
                      renderReportsContent()
                    ) : (
                      <Text style={styles.editTablePlaceholder}>
                        Placeholder: {section} editor content
                      </Text>
                    )}
                  </View>
                </View>

                {index < sectionLabels.length - 1 ? (
                  <View style={styles.editTableSpacerRow}>
                    <View style={[styles.editTableSpacerLabelCell, isNarrow && styles.editTableLabelCellNarrow]} />
                    <View style={styles.editTableSpacerContentCell} />
                  </View>
                ) : null}
              </React.Fragment>
            ))}
          </View>

          {saveError ? (
            <View style={styles.saveErrorBox}>
              <Text style={styles.saveErrorText}>{saveError}</Text>
            </View>
          ) : null}

        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
  },
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 30,
    minHeight: 30,
    paddingVertical: 0,
  },
  toggleIcon: {
    fontSize: 15,
    lineHeight: 15,
    color: EDITOR_COLORS.textSecondary,
  },
  toggleImage: {
    width: 9,
    height: 9,
  } as ImageStyle,
  toggleCell: {
    width: '8%',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 26,
  },
  firstNameCell: {
    width: '16%',
    paddingHorizontal: 6,
    justifyContent: 'center',
  },
  lastNameCell: {
    width: '16%',
    paddingHorizontal: 6,
    justifyContent: 'center',
  },
  // The summary row's cell widths must match the column header widths defined in
  // ManageClientsResultsList / RecentReportsResultsList (idCol 15, lastReportCol 16,
  // viewReportCol 15) — with a mismatch the View Report icons sit off-center of their header.
  idNumberCell: {
    width: '15%',
    paddingHorizontal: 6,
    justifyContent: 'center',
  },
  idCellInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  nextCell: {
    width: '14%',
    paddingHorizontal: 6,
    justifyContent: 'center',
  },
  nextCellNoId: {
    width: '20%',
    paddingHorizontal: 6,
    justifyContent: 'center',
  },
  lastReportCell: {
    width: '16%',
    paddingHorizontal: 6,
    justifyContent: 'center',
  },
  lastReportCellNoId: {
    width: '24%',
    paddingHorizontal: 6,
    justifyContent: 'center',
  },
  viewReportCell: {
    width: '15%',
    paddingHorizontal: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewReportCellNoId: {
    width: '16%',
    paddingHorizontal: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Applied on top of viewReportCell/viewReportCellNoId — widens the summary row's "View
  // Report" cell to lay the inline-view + PDF icons out side by side, matching the expanded
  // Reports table's own two-icon View column (see reportsColViewIcons).
  viewReportCellIcons: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 4,
  },
  cellValue: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  inlineInput: {
    ...editorText,
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
    width: '100%',
    maxWidth: 92,
  },
  inlineInputMobile: {
    fontFamily: undefined,
    paddingVertical: 0,
  },
  lockBtn: {
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  lockBtnText: {
    fontSize: 13,
    lineHeight: 13,
  },
  reportLinkBtn: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  reportLinkIcon: {
    fontSize: 16,
  },
  // Expanded state: the hairline-bordered card wraps the profile header row AND the edit
  // panel below it, so the header controls (arrow, name inputs, ...) sit inside the border too.
  rowExpanded: {
    borderRadius: 10,
    backgroundColor: EDITOR_COLORS.panelBackground,
    paddingTop: 4,
    marginVertical: 6,
  },
  // Expanded area under the header row — no border of its own (rowExpanded carries it); the
  // sections inside are tinted sub-cards (editTableRow). Bottom radius follows the card's
  // corners so this background never squares them off.
  editPanel: {
    padding: 10,
    paddingTop: 14,
    marginBottom: 2,
    borderBottomLeftRadius: 9,
    borderBottomRightRadius: 9,
    backgroundColor: EDITOR_COLORS.panelBackground,
  },
  editTable: {
    width: '100%',
    borderRadius: 4,
    overflow: 'hidden',
  },
  // One sub-card per section (Client Info, Schedule, Reports, ...). The label cell's right
  // separator stays as the divider between label and content inside the card.
  // No explicit width here (was '100%') — flex stretch already fills editPanel's cross axis by
  // default, and combining that with marginHorizontal is what actually shrinks + centers the
  // card by 50px total; width:'100%' plus a margin would instead overflow the parent by 50px.
  editTableRow: {
    marginHorizontal: 25,
    flexDirection: 'row',
    minHeight: 30,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: EDITOR_COLORS.panelSectionBorder,
    backgroundColor: EDITOR_COLORS.panelSectionBackground,
  },
  editTableRowLast: {},
  editTableSpacerRow: {
    width: '100%',
    flexDirection: 'row',
    minHeight: 10,
  },
  editTableSpacerLabelCell: {
    width: 100,
  },
  // flex:1, not a % width — editTableLabelCell next to it is a fixed pixel width, so a
  // percentage here only ever adds up to the row's full width at one specific row width
  // (label + content% = row width). Below that width the two overflow past the row/card
  // edge instead of sharing it — flex:1 always claims exactly "whatever's left", at any width.
  editTableSpacerContentCell: {
    flex: 1,
  },
  editTableLabelCell: {
    width: 100,
    borderRightWidth: 1,
    borderRightColor: EDITOR_COLORS.separator,
    paddingHorizontal: 8,
    paddingTop: 10,
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
  },
  // Narrow screens: the label column is otherwise a disproportionate share of an already-narrow
  // row (see isNarrow) — shrink it and tighten its padding so more width goes to the content
  // column, which is what actually needs it.
  editTableLabelCellNarrow: {
    width: 64,
    paddingHorizontal: 4,
  },
  editTableLabel: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    textAlign: 'right',
  },
  // The Schedule section's content starts one row lower than other sections (no header
  // text above its first row), so its own row label needs to drop to keep lining up with
  // the "Due" label instead of sitting flush with the content cell's top edge.
  editTableLabelScheduleOffset: {
    marginTop: 6,
  },
  // flex:1, not a % width — see editTableSpacerContentCell's comment; same overflow bug, same fix.
  editTableContentCell: {
    flex: 1,
    paddingHorizontal: 10,
    paddingTop: 4,
    paddingBottom: 8,
    justifyContent: 'flex-start',
  },
  editTablePlaceholder: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  clientInfoGrid: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: -4,
  },
  clientInfoPrimaryCell: {
    flexBasis: 300,
    flexGrow: 1,
    minWidth: 240,
    borderRadius: 6,
    padding: 10,
    backgroundColor: EDITOR_COLORS.panelSectionBackground,
  },
  clientInfoSecondaryCell: {
    flexBasis: 320,
    flexGrow: 1,
    minWidth: 260,
    borderRadius: 6,
    padding: 8,
    backgroundColor: EDITOR_COLORS.panelSectionBackground,
  },
  // Same sizing as clientInfoPrimaryCell but without flexGrow — the manual scheduling
  // column shouldn't stretch to fill the row width when it's the only column shown
  // (touchpoint scheduling disabled).
  scheduleManualCell: {
    flexBasis: 300,
    flexGrow: 0,
    minWidth: 240,
    borderRadius: 6,
    padding: 10,
    backgroundColor: EDITOR_COLORS.panelSectionBackground,
  },
  // Narrow screens: these grid cells' minWidth (240/260) is wider than the content column often
  // has to give once the label column and card margins are accounted for, so wrapping to its
  // own line (flexWrap on the grid) alone doesn't stop it overflowing — this lets it actually
  // shrink to fit instead of holding a minimum wider than the space available.
  clientInfoCellNarrow: {
    flexBasis: '100%',
    minWidth: 0,
  },
  touchpointColumnShift: {
    marginLeft: 50,
  },
  scheduleUpdateRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    // minimalSubmitBtn already carries its own marginTop (8) — the cells above already
    // pad their own bottom edge too, so this just needs to not stack more space on top.
    marginTop: 0,
  },
  clientInfoHeader: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    marginBottom: 10,
  },
  sendMethodRow: {
    minHeight: 22,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
    gap: 8,
  },
  touchpointCheckboxRow: {
    minHeight: 16,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
    gap: 8,
  },
  scheduleStartDateRow: {
    paddingLeft: 24,
    marginBottom: 6,
  },
  sendMethodSpacerRow: {
    minHeight: 15,
  },
  radioBtn: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.textMuted,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: EDITOR_COLORS.panelSectionBackground,
  },
  radioDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: EDITOR_COLORS.accent,
  },
  sendMethodLabel: {
    ...editorText,
    width: 44,
    color: EDITOR_COLORS.textSecondary,
  },
  sendMethodLabelWide: {
    ...editorText,
    width: 86,
    color: EDITOR_COLORS.textSecondary,
  },
  sendMethodInput: {
    ...editorText,
    flex: 1,
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.panelSectionBackground,
  },
  mobileDateInputBtn: {
    flex: 1,
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    justifyContent: 'center',
    backgroundColor: EDITOR_COLORS.panelSectionBackground,
  },
  mobileDateInputText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  mobileDateInputPlaceholder: {
    color: EDITOR_COLORS.placeholder,
  },
  sendMethodStaticValue: {
    ...editorText,
    flex: 1,
    color: EDITOR_COLORS.textPrimary,
  },
  requestInProcessText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    fontStyle: 'italic',
  },
  dropdownInlineBtn: {
    flex: 1,
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: EDITOR_COLORS.panelSectionBackground,
  },
  dropdownInlineBtnDisabled: {
    opacity: 0.5,
  },
  dropdownInlineText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  dropdownInlineChevron: {
    fontFamily: 'Arial',
    fontSize: 11,
    color: EDITOR_COLORS.textMuted,
  },
  sendNowBtn: {
    height: 32,
    paddingHorizontal: 16,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.action,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendNowBtnDisabled: {
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
  },
  requestStatusHeader: {
    marginTop: 8,
  },
  notifyStatusList: {
    marginBottom: 6,
  },
  notifyStatusRow: {
    minHeight: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 1,
  },
  notifyStatusDateCol: {
    width: 76,
  },
  notifyStatusTypeCol: {
    width: 60,
  },
  notifyStatusSentCol: {
    flex: 1,
  },
  notifyStatusType: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  notifyStatusValue: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  // Nested inline within the Link radio row's value column (see renderClientInfoContent) —
  // flex:1 so it fills the same space sendMethodStaticValue would otherwise take.
  secureLinkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  secureLinkTextWrap: {
    flex: 1,
  },
  secureLinkText: {
    ...editorText,
    color: EDITOR_COLORS.accent,
    textDecorationLine: 'underline',
    flexShrink: 1,
  },
  copyIconBtn: {
    paddingHorizontal: 4,
    paddingVertical: 2,
  },
  copyIconText: {
    fontSize: 15,
  },
  copyFeedbackText: {
    ...editorText,
    color: EDITOR_COLORS.successText,
  },
  cancelRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  sendNowRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 8,
  },
  cancelBtn: {
    width: 120,
    height: 28,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.submitBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendNowBtnText: {
    ...editorText,
    color: EDITOR_COLORS.actionText,
  },
  sendNowErrorText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    marginTop: 6,
  },
  minimalSubmitBtn: {
    marginTop: 8,
    marginLeft: 'auto',
    width: 120,
    height: 28,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.submitBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  minimalSubmitBtnTight: {
    marginTop: 0,
  },
  minimalSubmitBtnDisabled: {
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
  },
  minimalSubmitBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  checkboxBtn: {
    width: 18,
    height: 18,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.textMuted,
    borderRadius: 3,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: EDITOR_COLORS.panelSectionBackground,
    marginRight: 8,
  },
  checkboxMark: {
    fontFamily: 'Arial',
    fontSize: 12,
    color: EDITOR_COLORS.textPrimary,
    lineHeight: 12,
  },
  reportsTable: {
    width: '100%',
    borderRadius: 4,
    overflow: 'hidden',
  },
  reportsHeaderRow: {
    minHeight: 28,
    flexDirection: 'row',
    alignItems: 'center',
  },
  reportsHeaderCell: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    paddingHorizontal: 8,
  },
  reportsDataRow: {
    minHeight: 30,
    flexDirection: 'row',
    alignItems: 'center',
  },
  reportsDataCell: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    paddingHorizontal: 8,
  },
  // Column widths sum to 100 for BOTH the header and data rows — the View column used to be 8%
  // in the header but 14% in the data cells (overflowing the row), so its icons never lined up
  // with the "View" label.
  reportsColLocation: {
    width: '21%',
  },
  reportsColAssessment: {
    width: '22%',
  },
  // Pro (accessType 10) accounts don't get a Location column (single-practice, so it's never
  // meaningful) — applied on top of reportsColAssessment/Completed/Report to redistribute its
  // 21% among them, keeping the row at 100% total.
  reportsColAssessmentWide: {
    width: '30%',
  },
  reportsColCompleted: {
    width: '19%',
  },
  reportsColCompletedWide: {
    width: '23%',
  },
  reportsColView: {
    width: '14%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // "View" header label — a Text (unlike the data cell's View), so it needs textAlign to center
  // over the icon cluster below it.
  reportsColViewHeader: {
    textAlign: 'center',
  },
  // Applied on top of reportsColView for the data cell only — lays the inline-view (+ PDF,
  // unless the skin's enablereportpdf moved it) icons out side by side, centered in the column.
  reportsColViewIcons: {
    flexDirection: 'row',
    gap: 8,
  },
  reportsColReport: {
    width: '24%',
    paddingRight: 10,
  },
  reportsColReportWide: {
    width: '33%',
  },
  pdfIconImage: {
    width: 18,
    height: 18,
  } as ImageStyle,
  reportsEmptyText: {
    ...editorText,
    paddingHorizontal: 8,
    color: EDITOR_COLORS.textMuted,
  },
  phiRow: {
    minHeight: 30,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  phiName: {
    ...editorText,
    width: 110,
    paddingRight: 8,
    textAlign: 'right',
    color: EDITOR_COLORS.textSecondary,
  },
  phiValueInput: {
    ...editorText,
    flex: 1,
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.panelSectionBackground,
  },
  saveErrorBox: {
    backgroundColor: EDITOR_COLORS.errorBackground,
    borderRadius: 8,
    padding: 10,
    marginTop: 12,
    marginBottom: 10,
  },
  saveErrorText: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
  },
});
